import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { SearchSelect } from "@/components/ui/search-select";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";
import { fmtQty, type StockUnitRecord } from "@/types/inventory";

export interface PickedRoll {
  unit: StockUnitRecord;
  /** What to take; the whole roll when empty */
  qty: string;
}

/**
 * Chooses rolls from stock to take out for production or a job worker. Shows rolls that are in stock (not those
 * held for an order), filtered by product or by roll / lot number. Each ticked roll can be taken whole or in part.
 */
export function RollPicker({ picked, onChange, onlyUnit }: { picked: PickedRoll[]; onChange: (next: PickedRoll[]) => void; onlyUnit?: string }) {
  const [product, setProduct] = useState({ id: "", label: "" });
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<StockUnitRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ status: "IN_STOCK", page_size: "50" });
        if (product.id) qs.set("product_id", product.id);
        if (search.trim()) qs.set("search", search.trim());
        setRows(unwrapPage<StockUnitRecord>(await api.get(`/api/Inventory/Units?${qs}`)).rows);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, search.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [product.id, search]);

  const pickedIds = new Set(picked.map((p) => p.unit.id));
  const toggle = (u: StockUnitRecord) => onChange(pickedIds.has(u.id) ? picked.filter((p) => p.unit.id !== u.id) : [...picked, { unit: u, qty: "" }]);
  const visible = onlyUnit ? rows.filter((u) => u.unit.toUpperCase() === onlyUnit.toUpperCase()) : rows;
  const total = picked.reduce((a, p) => a + (p.qty ? Number(p.qty) || 0 : Number(p.unit.quantity)), 0);
  const units = [...new Set(picked.map((p) => p.unit.unit))];

  return (
    <div className="space-y-3 text-sm">
      <div className="grid sm:grid-cols-2 gap-2">
        <SearchSelect
          endpoint="/api/Product/List"
          extraParams={{ active: "true" }}
          value={product.id}
          selectedLabel={product.label}
          getLabel={(row) => `${row.name} (${row.code})`}
          placeholder="Any product"
          allowEmpty
          emptyLabel="Any product"
          onChange={(id, row) => setProduct({ id, label: row ? `${row.name} (${row.code})` : "" })}
        />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Roll or lot number" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-10 text-sm" />
        </div>
      </div>
      <div className="rounded-lg border max-h-64 overflow-y-auto divide-y">
        {loading && <div className="p-3 text-muted-foreground flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> Loading rolls…</div>}
        {!loading && visible.length === 0 && <div className="p-3 text-muted-foreground">No rolls in stock match.{onlyUnit ? ` Only rolls counted in ${onlyUnit} can go on this challan.` : ""}</div>}
        {visible.map((u) => {
          const p = picked.find((x) => x.unit.id === u.id);
          return (
            <label key={u.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 ${p ? "bg-primary/5" : ""}`}>
              <input type="checkbox" checked={!!p} onChange={() => toggle(u)} className="size-4" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.product?.name}{u.variant ? ` · ${u.variant.name}` : ""}</div>
                <div className="text-[11px] text-muted-foreground">Roll {u.unit_no} · lot {u.lot?.lot_number} · {u.warehouse?.name}{u.location ? ` · ${u.location}` : ""}</div>
              </div>
              <div className="text-xs font-semibold whitespace-nowrap">{fmtQty(u.quantity, u.unit)}</div>
            </label>
          );
        })}
      </div>
      {picked.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
          <div className="text-xs font-semibold">Taking {picked.length} roll{picked.length === 1 ? "" : "s"}: {Math.round(total * 1000) / 1000} {units.join(" + ")}</div>
          {picked.map((p) => (
            <div key={p.unit.id} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate">Roll {p.unit.unit_no} ({fmtQty(p.unit.quantity, p.unit.unit)})</span>
              <Input
                inputMode="decimal"
                placeholder="All"
                value={p.qty}
                onChange={(e) => onChange(picked.map((x) => (x.unit.id === p.unit.id ? { ...x, qty: e.target.value } : x)))}
                className="h-8 w-24 text-xs"
                title="Leave empty to take the whole roll"
              />
              <span className="w-8 text-muted-foreground">{p.unit.unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const pickedToItems = (picked: PickedRoll[]) => picked.map((p) => ({ unit_id: p.unit.id, ...(p.qty.trim() ? { quantity: Number(p.qty) } : {}) }));
